"use client";
import Image from "next/image";

import { useState } from "react";
import Link from "next/link";
import CalculatorShell from "@/components/CalculatorShell";
import CitationSection from "@/components/CitationSection";
import InputField from "@/components/InputField";
import SelectField from "@/components/SelectField";
import CheckboxField from "@/components/CheckboxField";
import ResultCard from "@/components/ResultCard";
import FaqSection from "@/components/FaqSection";
import RelatedCalculators from "@/components/RelatedCalculators";
import Icon from "@/components/Icon";
import { calcDeck, type DeckResult, type DeckMaterial, type DeckHeight, type DeckMode } from "@/lib/formulas";
import { CALCULATOR_SPECS } from "@/lib/calculator-info-data";

const MATERIAL_OPTIONS = [
  { value: "pressure_treated", label: "Pressure-Treated Wood — most affordable" },
  { value: "composite",        label: "Composite — low maintenance (Trex, Fiberon)" },
  { value: "hardwood",         label: "Hardwood — premium (Ipe, Tigerwood)" },
];
const HEIGHT_OPTIONS = [
  { value: "low",    label: "Ground level (under 18 inches off ground)" },
  { value: "medium", label: "Elevated (18–30 inches)" },
  { value: "high",   label: "Second story / high deck (30+ inches)" },
];
const MODE_OPTIONS = [
  { value: "installed", label: "Professionally installed (labor + materials)" },
  { value: "diy",       label: "DIY materials only (no labor cost)" },
];

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function CalculatorClient() {
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [material, setMaterial] = useState<DeckMaterial>("pressure_treated");
  const [height, setHeight] = useState<DeckHeight>("low");
  const [mode, setMode] = useState<DeckMode>("installed");
  const [includeStairs, setIncludeStairs] = useState(true);
  const [includeRailing, setIncludeRailing] = useState(true);
  const [result, setResult] = useState<DeckResult | null>(null);

  function handleCalculate() {
    setResult(calcDeck({
      lengthFt: parseFloat(length) || 0,
      widthFt:  parseFloat(width) || 0,
      material,
      height,
      mode,
      includeStairs,
      includeRailing,
    }));
  }

  const spec = CALCULATOR_SPECS.deck;

  return (
    <CalculatorShell
      name="Deck Cost Calculator"
      description="Get an installed or DIY deck cost estimate for pressure-treated wood, composite, or hardwood decking — stairs and railing included."
      icon="deck"
      category="high-value"
      accentColor="#4ade80"
      hasResult={result !== null}
      onCalculate={handleCalculate}
      disclaimer="Deck permit requirements, setback rules, and footing depth vary by municipality. Check local codes before building. Lumber prices fluctuate — verify with your supplier."
      inputsPanel={
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <InputField id="deck-length" label="Deck Length" unit="ft" type="number" min="0" step="1" placeholder="16" value={length} onChange={(e) => { setLength(e.target.value); setResult(null); }} />
            <InputField id="deck-width" label="Deck Width" unit="ft" type="number" min="0" step="1" placeholder="12" value={width} onChange={(e) => { setWidth(e.target.value); setResult(null); }} />
          </div>
          <SelectField id="deck-material" label="Decking Material" value={material} onChange={(e) => { setMaterial(e.target.value as DeckMaterial); setResult(null); }} options={MATERIAL_OPTIONS} hint={`${spec.buyingGuide.compositeDeckBrands.value}`} />
          <SelectField id="deck-height" label="Deck Height" value={height} onChange={(e) => { setHeight(e.target.value as DeckHeight); setResult(null); }} options={HEIGHT_OPTIONS} hint="Elevated decks require taller posts and footings, raising cost" />
          <SelectField id="deck-mode" label="Labor" value={mode} onChange={(e) => { setMode(e.target.value as DeckMode); setResult(null); }} options={MODE_OPTIONS} />
          <CheckboxField id="include-stairs" label="Include stairs" hint="Avg stair cost: $1,500–$2,500 depending on deck height" checked={includeStairs} onChange={(v) => { setIncludeStairs(v); setResult(null); }} />
          <CheckboxField id="include-railing" label="Include railing" hint="~$100/linear ft installed · Required by code for elevated decks" checked={includeRailing} onChange={(v) => { setIncludeRailing(v); setResult(null); }} />
        </div>
      }
      resultsPanel={
        result && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <ResultCard label="Deck Area" value={result.sqFt} unit="sq ft" accent />
              <ResultCard label={`${mode === "diy" ? "Material" : "Installed"} Cost/sq ft`} value={`$${result.costPerSqFt}`} accent />
            </div>
            <div className="py-5">
              <p className="font-mono text-[9px] font-bold uppercase tracking-widest mb-3" style={{ color: "var(--ink-faint)" }}>Total Cost Range</p>
              <p className="text-[2.6rem] leading-none font-black tracking-tight mb-2" style={{ color: "var(--ink)" }}>{fmt(result.totalMin)} – {fmt(result.totalMax)}</p>
              <p className="text-[11px] font-mono" style={{ color: "var(--ink-faint)" }}>{mode === "diy" ? "materials only" : "installed · labor + materials"} · ±20% regional variance</p>
            </div>
            <div className="pt-4 mt-2">
              <div className="font-mono text-[9px] font-bold uppercase tracking-wider text-[var(--ink-faint)] pb-2 select-none">
                Cost Breakdown
              </div>
              <div className="flex flex-col border-b border-[var(--line)]">
                {[
                  { label: `Deck surface (${result.sqFt} sq ft)`, value: fmt(result.baseCost) },
                  ...(result.stairCost > 0 ? [{ label: "Stairs", value: fmt(result.stairCost) }] : []),
                  ...(result.railingCost > 0 ? [{ label: `Railing (${result.railingLf} lin ft)`, value: fmt(result.railingCost) }] : []),
                  { label: "Total midpoint", value: fmt(result.totalMid), bold: true },
                ].map((row, i) => (
                  <div
                    key={i}
                    className={`flex justify-between items-center py-2.5 px-3 border-t border-[var(--line)] transition-all ${
                      row.bold
                        ? "border-l-3 border-l-[var(--accent)] pl-2 bg-[var(--surface-muted)]/30"
                        : "border-l-3 border-l-transparent"
                    }`}
                  >
                    <span className={`text-[12.5px] font-sans ${row.bold ? "text-[var(--accent)] font-extrabold" : "text-[var(--ink-soft)] font-semibold"}`}>
                      {row.label}
                    </span>
                    <span className={`font-mono text-[13px] ${row.bold ? "text-[var(--accent)] font-extrabold" : "text-[var(--ink)] font-bold"}`}>
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="info-box flex items-start gap-2">
              <Icon name="info" className="w-4 h-4 mt-0.5 shrink-0" />
              <span><strong>ROI:</strong> New decks recover ~{spec.buyingGuide.avgDeckRoi.value}% at resale with a Joy Score of 9.3/10 (NAR 2025). Composite decks command higher resale value due to lower long-term maintenance.</span>
            </div>
          </div>
        )
      }
    >
      {/* ── Informational Grid ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center text-[var(--ink)]">
        <div className="space-y-4 md:order-1">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">Decking Material Comparison Guide</h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Choosing the right <Link href="/articles/cost-to-build-a-deck-guide/" className="text-[var(--accent)] hover:underline font-semibold">decking material</Link> has long-term financial implications beyond the initial installation cost. Pressure-treated pine is the most affordable at $40–$55 per sq ft installed but requires annual sealing or staining to maintain its appearance and longevity. Composite decking from brands like <strong>{spec.buyingGuide.compositeDeckBrands.value}</strong> now makes up over 45% of new deck installations — it costs more upfront but carries 25–30 year warranties and requires no periodic staining. One critical consideration: <strong>{spec.buyingGuide.lumberPricesVolatile.value}</strong>, which means getting current quotes from your local lumber yard and understanding the <Link href="/articles/best-time-for-renovations/" className="text-[var(--accent)] hover:underline font-semibold">best season for renovations</Link> before finalizing your budget is essential.
          </p>
        </div>
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-2">
          <Image src="/images/deck_buying_guide.webp" alt="Composite deck with hidden fasteners, built-in lighting, and glass panel railing system on a two-story home" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" priority />
        </div>

        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-3">
          <Image src="/images/deck_site_prep.webp" alt="Deck footings being poured below frost line with tube forms and rebar prior to post installation" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" loading="lazy" />
        </div>
        <div className="space-y-4 md:order-4">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">Permits, Footings &amp; Code Compliance</h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            A permit is <strong>required</strong> for any deck attached to the house or elevated over 30 inches under <strong>{spec.sitePrep.permitRequired.standardCode}</strong>. Permit applications must include structural drawings detailing footing diameter, post spacing, beam span tables, and ledger attachment hardware. Footings must extend <strong>{spec.sitePrep.footingDepth.value}</strong> — anything above this level will heave and shift during freeze-thaw cycles. Railing is mandatory for elevated decks: under IRC Section R312, rails must be <strong>{spec.industryStandards.railingHeightRequirement.value}</strong>, with balusters spaced so a 4-inch sphere cannot pass through.
          </p>
        </div>
      </div>

      <CitationSection
        standardCode={spec.industryStandards.railingHeightRequirement.standardCode}
        sourceName={spec.industryStandards.railingHeightRequirement.sourceName}
        sourceUrl={spec.industryStandards.railingHeightRequirement.sourceUrl}
        paragraphs={[
          <p key="p1">
            Guard railing on decks is one of the most frequently cited code violations in residential construction. Under <b>{spec.industryStandards.railingHeightRequirement.standardCode}</b>, any deck surface more than 30 inches above the ground must be protected by a guard rail. The required height is <b>{spec.industryStandards.railingHeightRequirement.value}</b>. Baluster spacing must be tight enough that a 4-inch sphere — roughly the size of a small child&apos;s head — cannot pass through any opening, including the gap between the bottom rail and the decking surface.
          </p>,
          <p key="p2">
            Under the 2021 IRC update, horizontal rail designs that create a ladder-like climbing surface are explicitly prohibited, as they pose a significant fall hazard for young children. Composite railing systems with built-in baluster spacing tools make code compliance straightforward and are available in aluminum, vinyl, glass, and steel cable varieties. If your deck is in a coastal zone or wind-exposed area, verify that your railing system carries wind-load ratings appropriate for your region&apos;s design wind speed per ASCE 7.
          </p>
        ]}
        keyRequirements={[
          "Guard rails are required on all deck surfaces more than 30 inches above ground level.",
          "Minimum height of the deck guard rail must be 36 inches.",
          "Baluster spacing must prevent a 4-inch sphere from passing through.",
          "Horizontal rail designs that allow climbing are prohibited in the 2021 IRC update."
        ]}
      />

      {/* Featured Guide Banner */}
      <div className="my-8 p-5 bg-[var(--surface-muted)] border border-[var(--border)] rounded-lg flex flex-col sm:flex-row items-center justify-between gap-6 card-blueprint text-left">
        <div className="flex-1 flex flex-col gap-2">
          <span className="text-[9px] font-mono text-[var(--accent)] uppercase font-bold tracking-wider">Featured Deck Guide</span>
          <h4 className="text-base font-bold text-[var(--ink)] m-0">Deck Construction Cost: Material Comparison & Building Guide</h4>
          <p className="text-xs text-[var(--ink-soft)] leading-relaxed m-0">
            Compare prices for wood and composite decking boards, understand foundation rules, and find hidden ledger flashing details.
          </p>
        </div>
        <Link 
          href="/articles/cost-to-build-a-deck-guide/" 
          className="px-4 py-2 bg-[var(--accent)] text-white hover:bg-[var(--accent)]/90 text-xs font-mono font-bold uppercase tracking-wider rounded transition-all shrink-0 cursor-pointer text-center"
        >
          Read Deck Guide &rarr;
        </Link>
      </div>

      <FaqSection calculatorId="deck" />
      <RelatedCalculators calculatorId="deck" />
    </CalculatorShell>
  );
}
